import type { FlowDocument } from "./types";

/**
 * Persists the designer's in-progress document across a reload — one slot,
 * not one per example: reloading restores whatever was most recently being
 * worked on, remembering which example it started from so the toolbar's
 * dropdown and a "Reset" action both still make sense against it. Every
 * document mutation already goes through FlowPreview's own `setDocument`
 * (a deliberate, discrete edit — drag-stop, add/remove, connect, an
 * Inspector Apply, never a raw keystroke), so saving on every `document`
 * change needs no debouncing of its own.
 *
 * Takes a storage object shaped exactly like `window.localStorage`
 * (`getItem`/`setItem`/`removeItem`) rather than reading `window` directly,
 * so this stays testable in this project's plain Node test environment
 * (vite.config.ts's `test.environment: "node"`) with a trivial in-memory
 * fake, the same reasoning nodeConfigForm.ts's pure functions already
 * follow — real storage and a test fake satisfy the identical shape.
 */
export interface DraftStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SavedDraft {
  exampleName: string;
  document: FlowDocument;
}

const STORAGE_KEY = "uvi.flow-designer.draft.v1";

function looksLikeFlowDocument(value: unknown): value is FlowDocument {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.start_node_id === "string" &&
    Array.isArray(candidate.nodes) &&
    Array.isArray(candidate.edges)
  );
}

function looksLikeSavedDraft(value: unknown): value is SavedDraft {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.exampleName === "string" && looksLikeFlowDocument(candidate.document);
}

/**
 * Returns `null` for anything not worth trusting — nothing saved yet,
 * corrupted JSON, or a shape too broken to safely hand to `layoutFlow`/
 * `requiredPorts`/etc. A structurally-sound but *schema-invalid* document
 * (a raw-JSON edit the diagnostics panel was already flagging when the
 * reload happened) is still restored as-is: that in-progress, still-wrong
 * state is exactly what autosave exists to bring back, not something to
 * silently discard.
 */
export function loadDraft(storage: DraftStorageLike): SavedDraft | null {
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return looksLikeSavedDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Storage can be full, disabled (private browsing) or unavailable — an
 * autosave failing is a convenience lost, never something a document edit
 * itself should fail over, so this swallows any storage error rather than
 * throwing back into the caller's own state update.
 */
export function saveDraft(storage: DraftStorageLike, draft: SavedDraft): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // See above.
  }
}

export function clearDraft(storage: DraftStorageLike): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // See above.
  }
}
